import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Close,
  Star,
  AttachMoney,
  Schedule,
  TrendingUp,
  TrendingDown,
  Phone,
  Email,
  LocationOn,
  CalendarToday,
  Pets,
  CheckCircle,
  Cancel,
  Pending,
  Message,
  Pause,
  PlayArrow,
  Block,
  AdminPanelSettings,
  Apple,
  Android,
  DeviceUnknown,
  ThumbDown,
  ThumbUp,
  MoneyOff,
  AccountBalance,
} from '@mui/icons-material';

interface ServiceProvider {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  services: string[];
  status: 'active' | 'paused' | 'deactivated' | 'rejected';
  rating: number;
  totalBookings: number;
  totalEarnings: number;
  responseTime: number;
  lastContacted: string | null;
  joinDate: string;
  applicationDate?: string;
  rejectionDate?: string;
  rejectionReason?: string;
  rejectedBy?: string;
  location: {
    city: string;
    state: string;
  };
  completionRate: number;
  // Languages the provider speaks with customers (native-name display strings)
  spokenLanguages?: string[];
  isFoundingMember?: boolean;
}

interface Booking {
  id: string;
  petName: string;
  petOwner: string;
  service: string;
  startDate: string;
  endDate: string;
  status: 'completed' | 'cancelled' | 'in_progress' | 'pending' | 'rejected';
  amount: number;
  pawtelFee: number;
  providerPayout: number;
  rating?: number;
  review?: string;
}

interface Analytics {
  monthlyEarnings: { month: string; amount: number; pawtelFee: number }[];
  bookingTrends: { month: string; bookings: number }[];
  serviceBreakdown: { service: string; count: number; revenue: number }[];
  averageResponseTime: number;
  customerSatisfaction: number;
  repeatCustomerRate: number;
  // Booking request stats
  totalRequests: number;
  acceptedRequests: number;
  rejectedRequests: number;
  acceptedValue: number;
  rejectedValue: number;
  // Platform
  platform: 'ios' | 'android' | null;
  // Financial
  totalProviderPayout: number;
  totalPawtelRevenue: number;
  pawtelCommissionRate: number;
}

interface ServiceProviderDetailProps {
  provider: ServiceProvider;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

const PAWTEL_COMMISSION = 0.17; // 17% commission

const ServiceProviderDetail: React.FC<ServiceProviderDetailProps> = ({
  provider,
  open,
  onClose,
  onUpdate,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [foundingMember, setFoundingMember] = useState<boolean>(!!provider.isFoundingMember);
  const [savingFoundingMember, setSavingFoundingMember] = useState(false);

  useEffect(() => {
    setFoundingMember(!!provider.isFoundingMember);
  }, [provider.isFoundingMember, provider.id]);

  useEffect(() => {
    if (open && provider.id) {
      fetchProviderData();
    }
  }, [open, provider.id]);

  const fetchProviderData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

      // Try to fetch real data
      const [bookingsResponse, analyticsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/service-providers/${provider.id}/bookings`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/admin/service-providers/${provider.id}/analytics`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (bookingsResponse.ok) {
        const bookingsData = await bookingsResponse.json();
        setBookings(bookingsData.bookings || []);
      }

      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        setAnalytics(analyticsData);
      }

      // Mock data for development (will be replaced by real API data)
      const mockBookings: Booking[] = [
        {
          id: '1',
          petName: 'Buddy',
          petOwner: 'John Smith',
          service: 'Dog Walking',
          startDate: '2025-01-15T10:00:00Z',
          endDate: '2025-01-15T11:00:00Z',
          status: 'completed',
          amount: 25,
          pawtelFee: 3.75,
          providerPayout: 21.25,
          rating: 5,
          review: 'Excellent service! Buddy was very happy.',
        },
        {
          id: '2',
          petName: 'Luna',
          petOwner: 'Emma Johnson',
          service: 'Pet Boarding',
          startDate: '2025-01-10T18:00:00Z',
          endDate: '2025-01-12T10:00:00Z',
          status: 'completed',
          amount: 120,
          pawtelFee: 18,
          providerPayout: 102,
          rating: 4,
          review: 'Great care provided for Luna.',
        },
        {
          id: '3',
          petName: 'Max',
          petOwner: 'David Wilson',
          service: 'Dog Daycare',
          startDate: '2025-01-20T08:00:00Z',
          endDate: '2025-01-20T18:00:00Z',
          status: 'in_progress',
          amount: 45,
          pawtelFee: 6.75,
          providerPayout: 38.25,
        },
        {
          id: '4',
          petName: 'Bella',
          petOwner: 'Sarah Davis',
          service: 'Pet Boarding',
          startDate: '2025-01-22T09:00:00Z',
          endDate: '2025-01-24T17:00:00Z',
          status: 'rejected',
          amount: 90,
          pawtelFee: 13.50,
          providerPayout: 76.50,
        },
        {
          id: '5',
          petName: 'Charlie',
          petOwner: 'Mike Brown',
          service: 'Dog Walking',
          startDate: '2025-01-25T14:00:00Z',
          endDate: '2025-01-25T15:00:00Z',
          status: 'pending',
          amount: 25,
          pawtelFee: 3.75,
          providerPayout: 21.25,
        },
        {
          id: '6',
          petName: 'Daisy',
          petOwner: 'Lisa Taylor',
          service: 'Pet Boarding',
          startDate: '2025-01-18T16:00:00Z',
          endDate: '2025-01-19T10:00:00Z',
          status: 'cancelled',
          amount: 60,
          pawtelFee: 9,
          providerPayout: 51,
        },
      ];
      setBookings(mockBookings);

      const totalCompleted = mockBookings.filter(b => b.status === 'completed');
      const totalRejectedBookings = mockBookings.filter(b => b.status === 'rejected');
      const totalAccepted = mockBookings.filter(b => ['completed', 'in_progress'].includes(b.status));

      setAnalytics({
        monthlyEarnings: [
          { month: 'Oct', amount: 1200, pawtelFee: 180 },
          { month: 'Nov', amount: 1450, pawtelFee: 217.50 },
          { month: 'Dec', amount: 1680, pawtelFee: 252 },
          { month: 'Jan', amount: 1350, pawtelFee: 202.50 },
        ],
        bookingTrends: [
          { month: 'Oct', bookings: 28 },
          { month: 'Nov', bookings: 34 },
          { month: 'Dec', bookings: 42 },
          { month: 'Jan', bookings: 38 },
        ],
        serviceBreakdown: [
          { service: 'Dog Walking', count: 65, revenue: 1625 },
          { service: 'Pet Boarding', count: 25, revenue: 2500 },
          { service: 'Dog Daycare', count: 35, revenue: 1575 },
        ],
        averageResponseTime: 2.5,
        customerSatisfaction: 4.8,
        repeatCustomerRate: 68,
        // Booking request stats
        totalRequests: mockBookings.length,
        acceptedRequests: totalAccepted.length,
        rejectedRequests: totalRejectedBookings.length,
        acceptedValue: totalAccepted.reduce((s, b) => s + b.amount, 0),
        rejectedValue: totalRejectedBookings.reduce((s, b) => s + b.amount, 0),
        // Platform
        platform: 'ios',
        // Financial
        totalProviderPayout: totalCompleted.reduce((s, b) => s + b.providerPayout, 0),
        totalPawtelRevenue: totalCompleted.reduce((s, b) => s + b.pawtelFee, 0),
        pawtelCommissionRate: PAWTEL_COMMISSION * 100,
      });
    } catch (err) {
      console.error('Error fetching provider data:', err);
      setError('Failed to load provider data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: 'active' | 'paused' | 'deactivated') => {
    try {
      const token = localStorage.getItem('admin_token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

      const response = await fetch(`${API_BASE_URL}/api/admin/service-providers/${provider.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        onUpdate();
      } else {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      setError('Failed to update provider status. Please try again.');
    }
  };

  const handleToggleFoundingMember = async (enabled: boolean) => {
    // Optimistic update, revert on failure
    setFoundingMember(enabled);
    setSavingFoundingMember(true);
    try {
      const token = localStorage.getItem('admin_token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

      const response = await fetch(`${API_BASE_URL}/api/admin/set-founding-member/${provider.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isFoundingMember: enabled }),
      });

      if (response.ok) {
        onUpdate();
      } else {
        throw new Error('Failed to update Founding Member status');
      }
    } catch (err) {
      setFoundingMember(!enabled);
      setError('Failed to update Founding Member badge. Please try again.');
    } finally {
      setSavingFoundingMember(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'info' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'default';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'in_progress': return <Schedule />;
      case 'pending': return <Pending />;
      case 'cancelled': return <Cancel />;
      case 'rejected': return <ThumbDown />;
      default: return undefined;
    }
  };

  const getPlatformIcon = (platform: string | null) => {
    switch (platform) {
      case 'ios': return <Apple />;
      case 'android': return <Android sx={{ color: '#3DDC84' }} />;
      default: return <DeviceUnknown />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar src={provider.avatar} sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
              {provider.name.charAt(0)}
            </Avatar>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h5" fontWeight="bold">
                  {provider.name}
                </Typography>
                {analytics && getPlatformIcon(analytics.platform)}
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary">
                  Service Provider Dashboard
                </Typography>
                {analytics?.platform && (
                  <Chip
                    label={analytics.platform === 'ios' ? 'iOS' : 'Android'}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: 11 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: '80vh' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ p: 3 }}>
            <LinearProgress />
            <Typography align="center" sx={{ mt: 2 }}>
              Loading provider data...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Provider Info Header */}
            <Box sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><Email /></ListItemIcon>
                      <ListItemText primary={provider.email} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><Phone /></ListItemIcon>
                      <ListItemText primary={provider.phone} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><LocationOn /></ListItemIcon>
                      <ListItemText primary={`${provider.location.city}, ${provider.location.state}`} />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><CalendarToday /></ListItemIcon>
                      <ListItemText primary={`Joined ${formatDate(provider.joinDate)}`} />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    <Typography variant="subtitle2" sx={{ width: '100%', mb: 1 }}>
                      Services Offered:
                    </Typography>
                    {provider.services.map((service, index) => (
                      <Chip key={index} label={service} size="small" variant="outlined" />
                    ))}
                  </Box>
                  {provider.spokenLanguages && provider.spokenLanguages.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                      <Typography variant="subtitle2" sx={{ width: '100%', mb: 1 }}>
                        Supported Languages:
                      </Typography>
                      {provider.spokenLanguages.map((lang, index) => (
                        <Chip key={index} label={lang} size="small" variant="outlined" color="info" />
                      ))}
                    </Box>
                  )}
                  <Box display="flex" gap={1} mt={2}>
                    <Button
                      variant={provider.status === 'active' ? 'contained' : 'outlined'}
                      startIcon={<PlayArrow />}
                      size="small"
                      color="success"
                      onClick={() => handleStatusChange('active')}
                      disabled={provider.status === 'active'}
                    >
                      Activate
                    </Button>
                    <Button
                      variant={provider.status === 'paused' ? 'contained' : 'outlined'}
                      startIcon={<Pause />}
                      size="small"
                      color="warning"
                      onClick={() => handleStatusChange('paused')}
                      disabled={provider.status === 'paused'}
                    >
                      Pause
                    </Button>
                    <Button
                      variant={provider.status === 'deactivated' ? 'contained' : 'outlined'}
                      startIcon={<Block />}
                      size="small"
                      color="error"
                      onClick={() => handleStatusChange('deactivated')}
                      disabled={provider.status === 'deactivated'}
                    >
                      Deactivate
                    </Button>
                  </Box>
                  <Box mt={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={foundingMember}
                          onChange={(e) => handleToggleFoundingMember(e.target.checked)}
                          disabled={savingFoundingMember}
                          color="warning"
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" fontWeight={600}>
                            Founding Member badge
                          </Typography>
                          {foundingMember && (
                            <Chip label="⭐ Founding Member" size="small" color="warning" />
                          )}
                        </Box>
                      }
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Shows a Founding Member badge on this provider's listing and profile in the app.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                {provider.status !== 'rejected' ? (
                  [
                    <Tab key={0} label="Overview & Financials" />,
                    <Tab key={1} label="Booking History" />,
                    <Tab key={2} label="Performance Metrics" />,
                  ]
                ) : (
                  <Tab label="Rejection Details" />
                )}
              </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ p: 3 }}>
              {/* Rejection Details Tab */}
              {provider.status === 'rejected' && tabValue === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                          <Cancel color="error" />
                          <Typography variant="h5" fontWeight="bold" color="error.main">
                            Application Rejected
                          </Typography>
                        </Box>
                        <Divider sx={{ mb: 3 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <List>
                              <ListItem>
                                <ListItemIcon><CalendarToday /></ListItemIcon>
                                <ListItemText primary="Application Date" secondary={formatDate(provider.applicationDate || provider.joinDate)} />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><Cancel color="error" /></ListItemIcon>
                                <ListItemText primary="Rejection Date" secondary={provider.rejectionDate ? formatDate(provider.rejectionDate) : 'N/A'} />
                              </ListItem>
                              <ListItem>
                                <ListItemIcon><AdminPanelSettings /></ListItemIcon>
                                <ListItemText primary="Rejected By" secondary={provider.rejectedBy || 'Unknown Admin'} />
                              </ListItem>
                            </List>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="h6" gutterBottom>Rejection Reason</Typography>
                            <Paper sx={{ p: 2, backgroundColor: '#fafafa' }}>
                              <Typography variant="body1">
                                {provider.rejectionReason || 'No rejection reason provided.'}
                              </Typography>
                            </Paper>
                            <Box mt={3}>
                              <Typography variant="h6" gutterBottom>Services Applied For</Typography>
                              <Box display="flex" flexWrap="wrap" gap={1}>
                                {provider.services.map((service, index) => (
                                  <Chip key={index} label={service} size="small" variant="outlined" />
                                ))}
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                        <Divider sx={{ my: 3 }} />
                        <Box display="flex" gap={2}>
                          <Button variant="outlined" startIcon={<Message />}>Contact Applicant</Button>
                          <Button variant="contained" color="success" startIcon={<CheckCircle />} onClick={() => handleStatusChange('active')}>
                            Approve Application
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Overview & Financials Tab */}
              {provider.status !== 'rejected' && tabValue === 0 && analytics && (
                <Grid container spacing={3}>
                  {/* Top row: Key stats */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="text.secondary" variant="body2" gutterBottom>Provider Payouts</Typography>
                            <Typography variant="h4" fontWeight="bold" color="success.main">
                              {formatCurrency(analytics.totalProviderPayout)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {100 - analytics.pawtelCommissionRate}% of bookings
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: 'success.main' }}><AccountBalance /></Avatar>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="text.secondary" variant="body2" gutterBottom>Pawtel Revenue</Typography>
                            <Typography variant="h4" fontWeight="bold" color="primary.main">
                              {formatCurrency(analytics.totalPawtelRevenue)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {analytics.pawtelCommissionRate}% commission
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: 'primary.main' }}><AttachMoney /></Avatar>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="text.secondary" variant="body2" gutterBottom>Rating</Typography>
                            <Typography variant="h4" fontWeight="bold">
                              {provider.rating.toFixed(1)}
                            </Typography>
                            <Typography variant="body2" color="warning.main">
                              <Star sx={{ fontSize: 14, mr: 0.5, color: '#ffd700' }} />
                              {analytics.customerSatisfaction.toFixed(1)} satisfaction
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: 'warning.main' }}><Star /></Avatar>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="text.secondary" variant="body2" gutterBottom>Total Bookings</Typography>
                            <Typography variant="h4" fontWeight="bold">{provider.totalBookings}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {provider.completionRate}% completion
                            </Typography>
                          </Box>
                          <Avatar sx={{ bgcolor: 'info.main' }}><Pets /></Avatar>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Booking Requests Breakdown */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Booking Requests</Typography>
                        <Box mb={2}>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <ThumbUp color="success" sx={{ fontSize: 20 }} />
                              <Typography variant="body2">Accepted</Typography>
                            </Box>
                            <Box textAlign="right">
                              <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                                {analytics.acceptedRequests} bookings
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatCurrency(analytics.acceptedValue)}
                              </Typography>
                            </Box>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={analytics.totalRequests > 0 ? (analytics.acceptedRequests / analytics.totalRequests) * 100 : 0}
                            color="success"
                            sx={{ height: 8, borderRadius: 4, mb: 2 }}
                          />

                          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <ThumbDown color="error" sx={{ fontSize: 20 }} />
                              <Typography variant="body2">Rejected</Typography>
                            </Box>
                            <Box textAlign="right">
                              <Typography variant="subtitle2" fontWeight="bold" color="error.main">
                                {analytics.rejectedRequests} bookings
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatCurrency(analytics.rejectedValue)} lost
                              </Typography>
                            </Box>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={analytics.totalRequests > 0 ? (analytics.rejectedRequests / analytics.totalRequests) * 100 : 0}
                            color="error"
                            sx={{ height: 8, borderRadius: 4, mb: 2 }}
                          />

                          <Divider sx={{ my: 1 }} />
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" fontWeight="bold">Total Requests</Typography>
                            <Typography variant="body2" fontWeight="bold">{analytics.totalRequests}</Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Acceptance Rate</Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {analytics.totalRequests > 0 ? Math.round((analytics.acceptedRequests / analytics.totalRequests) * 100) : 0}%
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Revenue Breakdown (Provider vs Pawtel) */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Revenue Breakdown</Typography>
                        {analytics.monthlyEarnings.map((month, index) => (
                          <Box key={index} mb={2}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="body2" fontWeight="bold">{month.month}</Typography>
                              <Typography variant="body2">Total: {formatCurrency(month.amount)}</Typography>
                            </Box>
                            <Box display="flex" gap={0.5} mb={0.5}>
                              <Box
                                sx={{
                                  height: 8,
                                  borderRadius: '4px 0 0 4px',
                                  bgcolor: 'success.main',
                                  flex: month.amount - month.pawtelFee,
                                }}
                              />
                              <Box
                                sx={{
                                  height: 8,
                                  borderRadius: '0 4px 4px 0',
                                  bgcolor: 'primary.main',
                                  flex: month.pawtelFee,
                                }}
                              />
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" color="success.main">
                                Provider: {formatCurrency(month.amount - month.pawtelFee)}
                              </Typography>
                              <Typography variant="caption" color="primary.main">
                                Pawtel: {formatCurrency(month.pawtelFee)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'success.main' }} />
                            <Typography variant="body2">Provider Payout</Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: 'primary.main' }} />
                            <Typography variant="body2">Pawtel Commission ({analytics.pawtelCommissionRate}%)</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Service Performance */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Service Performance</Typography>
                        <Grid container spacing={2}>
                          {analytics.serviceBreakdown.map((service, index) => (
                            <Grid item xs={12} sm={4} key={index}>
                              <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold">{service.service}</Typography>
                                <Typography variant="h5" color="primary" fontWeight="bold">
                                  {formatCurrency(service.revenue)}
                                </Typography>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography variant="caption" color="text.secondary">
                                    {service.count} bookings
                                  </Typography>
                                  <Typography variant="caption" color="success.main">
                                    Provider: {formatCurrency(service.revenue * (1 - PAWTEL_COMMISSION))}
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={(service.count / Math.max(...analytics.serviceBreakdown.map(s => s.count))) * 100}
                                  sx={{ height: 4, borderRadius: 2, mt: 1 }}
                                />
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Booking History Tab */}
              {provider.status !== 'rejected' && tabValue === 1 && (
                <Box>
                  {/* Summary cards */}
                  <Grid container spacing={2} mb={3}>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="caption" color="text.secondary">Total</Typography>
                          <Typography variant="h5" fontWeight="bold">{bookings.length}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="caption" color="success.main">Completed</Typography>
                          <Typography variant="h5" fontWeight="bold" color="success.main">
                            {bookings.filter(b => b.status === 'completed').length}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="caption" color="error.main">Rejected/Cancelled</Typography>
                          <Typography variant="h5" fontWeight="bold" color="error.main">
                            {bookings.filter(b => ['rejected', 'cancelled'].includes(b.status)).length}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Card variant="outlined">
                        <CardContent sx={{ py: 1.5 }}>
                          <Typography variant="caption" color="error.main">Rejected Value</Typography>
                          <Typography variant="h5" fontWeight="bold" color="error.main">
                            {formatCurrency(bookings.filter(b => b.status === 'rejected').reduce((s, b) => s + b.amount, 0))}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell><strong>Pet & Owner</strong></TableCell>
                          <TableCell><strong>Service</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Total</strong></TableCell>
                          <TableCell><strong>Provider Payout</strong></TableCell>
                          <TableCell><strong>Pawtel Fee</strong></TableCell>
                          <TableCell><strong>Rating</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bookings.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              No bookings found
                            </TableCell>
                          </TableRow>
                        ) : (
                          bookings.map((booking) => (
                            <TableRow key={booking.id} hover sx={booking.status === 'rejected' ? { bgcolor: '#fff5f5' } : {}}>
                              <TableCell>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight="medium">{booking.petName}</Typography>
                                  <Typography variant="body2" color="text.secondary">{booking.petOwner}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={booking.service} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{formatDate(booking.startDate)}</Typography>
                                {booking.endDate !== booking.startDate && (
                                  <Typography variant="caption" color="text.secondary">to {formatDate(booking.endDate)}</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={booking.status.replace('_', ' ').toUpperCase()}
                                  size="small"
                                  color={getStatusColor(booking.status)}
                                  icon={getStatusIcon(booking.status)}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">{formatCurrency(booking.amount)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="success.main" fontWeight="medium">
                                  {formatCurrency(booking.providerPayout)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="primary.main" fontWeight="medium">
                                  {formatCurrency(booking.pawtelFee)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {booking.rating ? (
                                  <Box>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                      <Star sx={{ color: '#ffd700', fontSize: 16 }} />
                                      <Typography variant="body2">{booking.rating}/5</Typography>
                                    </Box>
                                    {booking.review && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 150 }} noWrap>
                                        {booking.review}
                                      </Typography>
                                    )}
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Performance Metrics Tab */}
              {provider.status !== 'rejected' && tabValue === 2 && analytics && (
                <Grid container spacing={3}>
                  {/* KPIs */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Key Performance Indicators</Typography>
                        <List>
                          <ListItem>
                            <ListItemText
                              primary="Completion Rate"
                              secondary={`${provider.completionRate}% of bookings completed successfully`}
                            />
                            <Chip
                              label={`${provider.completionRate}%`}
                              color={provider.completionRate >= 90 ? 'success' : provider.completionRate >= 70 ? 'warning' : 'error'}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemText
                              primary="Acceptance Rate"
                              secondary={`${analytics.totalRequests > 0 ? Math.round((analytics.acceptedRequests / analytics.totalRequests) * 100) : 0}% of requests accepted`}
                            />
                            <Chip
                              label={`${analytics.totalRequests > 0 ? Math.round((analytics.acceptedRequests / analytics.totalRequests) * 100) : 0}%`}
                              color={analytics.acceptedRequests / analytics.totalRequests >= 0.8 ? 'success' : 'warning'}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemText
                              primary="Repeat Customer Rate"
                              secondary={`${analytics.repeatCustomerRate}% of customers book again`}
                            />
                            <Chip label={`${analytics.repeatCustomerRate}%`} color="primary" />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemText
                              primary="Average Response Time"
                              secondary="Time to respond to booking requests"
                            />
                            <Chip
                              label={`${analytics.averageResponseTime}h`}
                              color={analytics.averageResponseTime <= 2 ? 'success' : analytics.averageResponseTime <= 4 ? 'warning' : 'error'}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemText
                              primary="Customer Satisfaction"
                              secondary="Average rating from completed bookings"
                            />
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Star sx={{ color: '#ffd700' }} />
                              <Typography variant="h6" fontWeight="bold">
                                {analytics.customerSatisfaction}/5
                              </Typography>
                            </Box>
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Financial Performance */}
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Financial Summary</Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon><AttachMoney color="primary" /></ListItemIcon>
                            <ListItemText
                              primary="Total Booking Value"
                              secondary={formatCurrency(provider.totalEarnings)}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemIcon><AccountBalance color="success" /></ListItemIcon>
                            <ListItemText
                              primary="Provider Payouts"
                              secondary={formatCurrency(analytics.totalProviderPayout)}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemIcon><AttachMoney color="primary" /></ListItemIcon>
                            <ListItemText
                              primary="Pawtel Revenue"
                              secondary={`${formatCurrency(analytics.totalPawtelRevenue)} (${analytics.pawtelCommissionRate}% commission)`}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemIcon><MoneyOff color="error" /></ListItemIcon>
                            <ListItemText
                              primary="Rejected Booking Value"
                              secondary={`${formatCurrency(analytics.rejectedValue)} from ${analytics.rejectedRequests} rejected bookings`}
                            />
                          </ListItem>
                          <Divider />
                          <ListItem>
                            <ListItemIcon><ThumbUp color="success" /></ListItemIcon>
                            <ListItemText
                              primary="Accepted Booking Value"
                              secondary={`${formatCurrency(analytics.acceptedValue)} from ${analytics.acceptedRequests} accepted bookings`}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Monthly Trends */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Monthly Booking Trends</Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell><strong>Month</strong></TableCell>
                                <TableCell align="right"><strong>Bookings</strong></TableCell>
                                <TableCell align="right"><strong>Total Revenue</strong></TableCell>
                                <TableCell align="right"><strong>Provider Payout</strong></TableCell>
                                <TableCell align="right"><strong>Pawtel Fee</strong></TableCell>
                                <TableCell align="right"><strong>Trend</strong></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {analytics.monthlyEarnings.map((month, index) => (
                                <TableRow key={index}>
                                  <TableCell>{month.month}</TableCell>
                                  <TableCell align="right">{analytics.bookingTrends[index]?.bookings || '-'}</TableCell>
                                  <TableCell align="right">{formatCurrency(month.amount)}</TableCell>
                                  <TableCell align="right" sx={{ color: 'success.main' }}>
                                    {formatCurrency(month.amount - month.pawtelFee)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: 'primary.main' }}>
                                    {formatCurrency(month.pawtelFee)}
                                  </TableCell>
                                  <TableCell align="right">
                                    {index > 0 && (
                                      month.amount > analytics.monthlyEarnings[index - 1].amount
                                        ? <TrendingUp color="success" sx={{ fontSize: 20 }} />
                                        : <TrendingDown color="error" sx={{ fontSize: 20 }} />
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceProviderDetail;
